"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import Cropper from "react-easy-crop"
import type { Area } from "react-easy-crop"
import { X, Upload, ZoomIn, ZoomOut, RotateCcw, Loader2, ImagePlus } from "lucide-react"
import { cn } from "@/lib/utils"
import { Z } from "@/lib/z-index"
import { useAnimatedClose } from "@/lib/use-animated-close"
import { uploadAvatar, ApiError } from "@/lib/api"
import { useImageCompress } from "@/lib/hooks/use-image-compress"
import { useToast } from "@/lib/hooks/use-toast"
import { CompressProgressModal } from "../compress-progress-modal"

/** 允许的图片类型 */
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"]
/** 最大文件大小 32MB */
const MAX_FILE_SIZE = 32 * 1024 * 1024
/** 裁剪输出尺寸（px） */
const OUTPUT_SIZE = 256

interface AvatarUploadModalProps {
  onClose: () => void
  onSuccess: (avatarUrl: string) => void
  currentAvatar?: string
}

/** 用 canvas 从原图中裁出 croppedAreaPixels 区域，输出为 OUTPUT_SIZE 的圆形 PNG Blob */
async function getCroppedBlob(
  imageSrc: string,
  pixelCrop: Area,
): Promise<Blob> {
  const image = await createImage(imageSrc)
  const canvas = document.createElement("canvas")
  canvas.width = OUTPUT_SIZE
  canvas.height = OUTPUT_SIZE
  const ctx = canvas.getContext("2d")!

  // 绘制裁剪区域到 canvas
  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    OUTPUT_SIZE,
    OUTPUT_SIZE,
  )

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error("Canvas toBlob failed"))
    }, "image/png")
  })
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.addEventListener("load", () => resolve(img))
    img.addEventListener("error", (e) => reject(e))
    img.crossOrigin = "anonymous"
    img.src = url
  })
}

export function AvatarUploadModal({
  onClose,
  onSuccess,
  currentAvatar,
}: AvatarUploadModalProps) {
  const { closing, requestClose } = useAnimatedClose(onClose, 300)
  const [entered, setEntered] = useState(false)
  const toast = useToast()
  const { compress, cancel: cancelCompress, compressing, progress } = useImageCompress()

  const [preview, setPreview] = useState<string | null>(null)
  const [error, setError] = useState("")
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)

  // react-easy-crop 状态
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(raf)
  }, [])

  // 释放 object URL
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview)
    }
  }, [preview])

  const validateFile = useCallback((f: File): string | null => {
    if (!ACCEPTED_TYPES.includes(f.type)) {
      return "仅支持 JPG、PNG、WebP、GIF 格式"
    }
    if (f.size > MAX_FILE_SIZE) {
      return "图片大小不能超过 32MB"
    }
    return null
  }, [])

  const handleFileSelect = useCallback(async (f: File) => {
    const err = validateFile(f)
    if (err) {
      setError(err)
      return
    }
    setError("")
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)

    // 大图先压缩再进裁剪器，避免浏览器加载巨图卡顿
    const compressed = await compress(f)
    if (!compressed) {
      toast.info("已取消图片压缩")
      return
    }

    if (preview) URL.revokeObjectURL(preview)
    setPreview(URL.createObjectURL(compressed))
  }, [preview, validateFile, compress, toast])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) handleFileSelect(f)
    e.target.value = ""
  }, [handleFileSelect])

  // ── 文件拖入 ──
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(false)
    const f = e.dataTransfer.files[0]
    if (f) handleFileSelect(f)
  }, [handleFileSelect])

  const onCropComplete = useCallback((_croppedArea: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels)
  }, [])

  const handleReset = useCallback(() => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
  }, [])

  const handleUpload = useCallback(async () => {
    if (!preview || !croppedAreaPixels) return
    setUploading(true)
    setError("")
    try {
      const blob = await getCroppedBlob(preview, croppedAreaPixels)
      const file = new File([blob], "avatar.png", { type: "image/png" })
      const { avatarUrl } = await uploadAvatar(file)
      onSuccess(avatarUrl)
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.statusText || "上传失败，请重试")
      } else {
        setError("上传失败，请重试")
      }
    } finally {
      setUploading(false)
    }
  }, [preview, croppedAreaPixels, onSuccess])

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300",
          entered && !closing ? "opacity-100" : "opacity-0",
        )}
        style={{ zIndex: Z.MODAL_OVERLAY }}
        onClick={requestClose}
        onKeyDown={(e) => e.key === "Escape" && requestClose()}
        role="button"
        tabIndex={0}
      />

      {/* Modal */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: Z.MODAL_CONTENT }}
      >
        <div
          className={cn(
            "relative w-full max-w-sm rounded-2xl liquid-glass-float shadow-2xl transition-all duration-300",
            entered && !closing
              ? "opacity-100 scale-100 translate-y-0"
              : "opacity-0 scale-95 translate-y-4",
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <h3 className="text-base font-semibold">编辑头像</h3>
            <button
              type="button"
              onClick={requestClose}
              className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-5 pb-5">
            {!preview ? (
              /* ── 选择文件 ── */
              <div
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                role="button"
                tabIndex={0}
                className={cn(
                  "flex flex-col items-center justify-center gap-3 py-12 rounded-xl border-2 border-dashed cursor-pointer transition-all",
                  dragOver
                    ? "border-primary bg-primary/5 scale-[1.01]"
                    : "border-white/20 hover:border-white/40 hover:bg-white/5",
                )}
              >
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center">
                  <ImagePlus className="w-7 h-7 text-muted-foreground" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium">点击或拖拽图片到此处</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    支持 JPG、PNG、WebP、GIF，最大 32MB
                  </p>
                </div>
              </div>
            ) : (
              /* ── 裁剪区域 ── */
              <div className="flex flex-col items-center gap-4">
                {/* Cropper 容器 */}
                <div className="relative w-56 h-56 rounded-xl overflow-hidden bg-black/40">
                  <Cropper
                    image={preview}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                    minZoom={1}
                    maxZoom={3}
                    style={{
                      containerStyle: { borderRadius: "0.75rem" },
                    }}
                  />
                </div>

                {/* 缩放控制 */}
                <div className="flex items-center gap-3 w-full max-w-[224px]">
                  <button
                    type="button"
                    onClick={() => setZoom((z) => Math.max(1, z - 0.1))}
                    className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                    title="缩小"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.01}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="flex-1 h-1.5 rounded-full appearance-none bg-white/20 accent-primary cursor-pointer [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:shadow-md"
                  />
                  <button
                    type="button"
                    onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
                    className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                    title="放大"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="p-1.5 rounded-full hover:bg-white/10 transition-colors"
                    title="重置"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </button>
                </div>

                {/* 换一张 */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  选择其他图片
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(",")}
              onChange={handleInputChange}
              className="hidden"
            />

            {error && (
              <p className="text-xs text-red-400 mt-3 text-center">
                {error}
              </p>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 mt-5">
              <button
                type="button"
                onClick={requestClose}
                disabled={uploading}
                className="flex-1 px-4 py-2.5 rounded-full liquid-glass-float text-sm font-medium hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleUpload}
                disabled={!preview || uploading}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    上传中…
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    确认上传
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 压缩进度弹窗 */}
      {compressing && (
        <CompressProgressModal
          progress={progress}
          onCancel={cancelCompress}
        />
      )}
    </>
  )
}
